import { FC, useEffect, useMemo, useState } from 'react';
import { SubmitButton } from '@/components/form/submit-button';
import { Button } from '@/components/ui/button';
import { strings } from '@/constants/strings';
import { useForm } from '@/forms/hooks/use-form';
import { UserFieldValues } from '@/forms/schemas/user/field-values';
import { buildUserFormSchema } from '@/forms/schemas/user/schema';
import { useGroupSelectOptions } from '@/hooks/use-group-select-options';
import { useOrganisationSelectOptions } from '@/hooks/use-organisation-select-options';
import { useRoleSelectOptions } from '@/hooks/use-role-select-options';
import { DrawerContentFormLayout } from '@/layouts/drawer-content-form-layout';
import { ApplicationRole } from '@/types/application-role';
import { ApplicationUser } from '@/types/application-user';
import { Group } from '@/types/group';
import { Organisation, OrganisationIdAndNameOnly } from '@/types/organisation';
import { getPlatformAdminRoleId } from '@/utils/get-platform-admin-role-id';

export type CreateUserFormProps = {
  activeUser: ApplicationUser;
  roles: ApplicationRole[];
  canAssignAnyOrganisation: boolean;
  currentOrganisation: OrganisationIdAndNameOnly | undefined;
  organisations: Organisation[];
  permittedGroupValues: Group[];
  groups: Group[];
  onCancel: () => void;
  onSubmit: (values: UserFieldValues) => void;
};

export const CreateUserForm: FC<CreateUserFormProps> = ({
  activeUser,
  roles,
  canAssignAnyOrganisation,
  currentOrganisation,
  organisations,
  groups,
  permittedGroupValues,
  onCancel,
  onSubmit,
}) => {
  const platformAdminRoleId = getPlatformAdminRoleId(roles);
  const [isPlatformAdminRoleSelected, setIsPlatformAdminRoleSelected] =
    useState(false);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<
    number | undefined
  >(currentOrganisation?.id);
  const roleOptions = useRoleSelectOptions(roles);
  const organisationOptions = useOrganisationSelectOptions(organisations);
  const filteredGroups = useMemo(
    () =>
      selectedOrganisationId
        ? groups.filter(
            (group) => group.organisation.id === selectedOrganisationId,
          )
        : groups,
    [groups, selectedOrganisationId],
  );
  const groupOptions = useGroupSelectOptions(filteredGroups);
  const permittedGroupValueOptions =
    useGroupSelectOptions(permittedGroupValues);

  const formSchema = useMemo(
    () =>
      buildUserFormSchema({
        type: 'create',
        activeUser,
        canAssignAnyOrganisation,
        isPlatformAdminRoleSelected,
        organisationName: currentOrganisation?.name ?? '',
        organisationOptions,
        roleOptions,
        groupOptions,
        permittedGroupValueOptions,
      }),
    [
      activeUser,
      canAssignAnyOrganisation,
      currentOrganisation?.name,
      isPlatformAdminRoleSelected,
      organisationOptions,
      roleOptions,
      groupOptions,
      permittedGroupValueOptions,
    ],
  );

  const { fieldItems, formState, handleSubmit, trigger, watch } = useForm({
    schema: formSchema,
    onSubmit,
  });

  useEffect(() => {
    if (formState.submitCount > 0) {
      void trigger();
    }
  }, [formSchema, formState.submitCount, trigger]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'roleId') {
        setIsPlatformAdminRoleSelected(value.roleId === platformAdminRoleId);
      }
      if (name === 'organisationId') {
        setSelectedOrganisationId(
          typeof value.organisationId === 'number'
            ? value.organisationId
            : undefined,
        );
      }
    });
    return () => subscription.unsubscribe();
  }, [platformAdminRoleId, setIsPlatformAdminRoleSelected, watch]);

  return (
    <DrawerContentFormLayout
      buttonItems={[
        <Button key="cancel" onClick={onCancel} variant="secondary">
          {strings.users.drawer_create_form.cancel}
        </Button>,
        <SubmitButton
          key="submit"
          text={strings.users.drawer_create_form.submit}
        />,
      ]}
      fieldItems={fieldItems}
      onSubmit={handleSubmit}
    />
  );
};
